/**
 * DefaultRedirect Component
 *
 * Handles redirecting to the first available services provider when no
 * recent provider is cached. This runs inside the guard chain, so auth
 * and services provider data are already loaded.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { OrdersTableTab } from '@ekhodealer/ekho-common';

import { useGetUserServicesProviders } from '@common/hooks/useGetUserServicesProviders';
import { paths } from '@config/paths';
import { SettingsSection } from '@config/paths/settingsSections';
import { POST_SETUP_REDIRECT_KEY } from '@config/storageKeys';
import { setRecentServicesProviderId } from '@app/utils/getFirstServicesProviderId';

export const DefaultRedirect: React.FC = () => {
	const navigate = useNavigate();
	const { servicesProviders, isLoading, hasError } = useGetUserServicesProviders();

	useEffect(() => {
		// Wait for providers to load
		if (isLoading) {
			console.log('[DefaultRedirect] Loading services providers...');
			return;
		}

		// Handle error state
		if (hasError) {
			console.error('[DefaultRedirect] Error loading services providers');
			return;
		}

		// Get the first provider (they're already sorted in useGetUserServicesProviders)
		const firstProvider = servicesProviders[0];

		if (firstProvider) {
			console.log(`[DefaultRedirect] Redirecting to first provider: ${firstProvider.id}`);
			// Cache this provider for future visits
			setRecentServicesProviderId(firstProvider.id);

			// After onboarding setup, redirect to AI BDC instead of orders
			const postSetupRedirect = localStorage.getItem(POST_SETUP_REDIRECT_KEY);
			if (postSetupRedirect === 'ai-bdc') {
				localStorage.removeItem(POST_SETUP_REDIRECT_KEY);
				navigate(paths.settingsSection.getHref(firstProvider.id, SettingsSection.ProductsAiBdc), { replace: true });
			} else {
				navigate(paths.orders.getHref(firstProvider.id, OrdersTableTab.ALL), { replace: true });
			}
		} else {
			// No providers available
			console.error('[DefaultRedirect] No services providers available for user');
		}
	}, [servicesProviders, isLoading, hasError, navigate]);

	// Show nothing while redirecting (guards handle loading states)
	return null;
};
